import {
  ClaimResearchRouteLabel,
  ClaimResearchVerdictLabel,
  IClaimResearchContractTextStatus,
  IClaimResearchRun,
} from "types/claimResearch";

/**
 * CLE-2965 — the pure half of the "AI Insights v2" tab: labels, timing thresholds and
 * small formatters, kept apart from the component so each is tested on its own.
 */

/** The same five values as clear-api's CLAIM_RESEARCH_VERDICTS, in the same order. */
export const CLAIM_RESEARCH_VERDICTS: ClaimResearchVerdictLabel[] = [
  "REAL_UNDERPAYMENT_APPEAL",
  "OUR_MODELING_ERROR",
  "BILLING_ERROR_CORRECTED_CLAIM",
  "CORRECT_PER_POLICY_NO_APPEAL",
  "INSUFFICIENT_DATA",
];

/** The same four values as clear-api's CLAIM_RESEARCH_ROUTES, in the same order. */
export const CLAIM_RESEARCH_ROUTES: ClaimResearchRouteLabel[] = [
  "APPEAL",
  "CORRECTED_CLAIM",
  "FIX_OUR_MODEL",
  "NO_ACTION",
];

export const VERDICT_LABELS: Record<ClaimResearchVerdictLabel, string> = {
  REAL_UNDERPAYMENT_APPEAL: "Real underpayment",
  OUR_MODELING_ERROR: "Our modeling error",
  BILLING_ERROR_CORRECTED_CLAIM: "Billing error",
  CORRECT_PER_POLICY_NO_APPEAL: "Paid correctly per policy",
  INSUFFICIENT_DATA: "Not enough data",
};

export const ROUTE_LABELS: Record<ClaimResearchRouteLabel, string> = {
  APPEAL: "Appeal",
  CORRECTED_CLAIM: "Send a corrected claim",
  FIX_OUR_MODEL: "Fix our model",
  NO_ACTION: "No action",
};

type TagSeverity = "success" | "info" | "warning" | "danger" | undefined;

export const VERDICT_SEVERITY: Record<ClaimResearchVerdictLabel, TagSeverity> =
  {
    REAL_UNDERPAYMENT_APPEAL: "danger",
    OUR_MODELING_ERROR: "warning",
    BILLING_ERROR_CORRECTED_CLAIM: "warning",
    CORRECT_PER_POLICY_NO_APPEAL: "success",
    INSUFFICIENT_DATA: "info",
  };

/** The labels are plain strings on the server while they settle: show an unknown one as it is. */
export const verdictLabel = (value: string | null | undefined) =>
  value
    ? VERDICT_LABELS[value as ClaimResearchVerdictLabel] ?? value
    : "No verdict";

export const routeLabel = (value: string | null | undefined) =>
  value ? ROUTE_LABELS[value as ClaimResearchRouteLabel] ?? value : "No route";

export const verdictSeverity = (value: string | null | undefined) =>
  value ? VERDICT_SEVERITY[value as ClaimResearchVerdictLabel] : undefined;

/**
 * Every sentence below is shown to the user. Keep them to what a reader needs to act on the
 * result — no product-spec text (how it works, cost, retention), and never the word
 * "independent" (Mike, 2026-10-29: the tab is "AI Insights v2").
 */
export const TAB_TITLE = "AI Insights v2";
export const RUN_BUTTON_LABEL = "Run AI analysis";
/** Once the claim has any run (Mike, 2026-10-29). */
export const RERUN_BUTTON_LABEL = "Re-run AI analysis";
export const NO_RUNS_TEXT = "No AI analysis has been run on this claim yet.";
export const DISABLED_TEXT =
  "AI analysis is switched off right now. Earlier results can still be read.";
export const ALREADY_RUNNING_TEXT =
  "An analysis was already in progress for this claim, so no new run was started. Showing that run.";
export const QUEUED_TEXT = "Waiting to start…";
export const QUEUED_UNPICKED_TEXT =
  "This is taking longer than usual to start.";
export const RUNNING_TEXT =
  "Researching payer policies and the contract — this usually takes 2–5 minutes.";
export const STUCK_TEXT =
  "This is taking much longer than usual and may have stopped.";
export const STAFF_REVIEW_TITLE = "ClearHealth staff review";
export const STAFF_REVIEW_NOTE =
  "Visible to ClearHealth staff only. Review this AI analysis and record whether you agree — your feedback is used to improve it.";
export const QUESTION_LABEL = "Open question from the AI";
export const NOTE_MAX_CHARS = 2000;

/** How often the tab re-reads a QUEUED / RUNNING run. */
export const POLL_MS = 4000;
/** How often the elapsed time re-renders while waiting. */
export const TICK_MS = 5000;
/** The worker polls every few seconds, so a run still QUEUED after this was likely never picked up. */
export const QUEUED_UNPICKED_AFTER_MS = 2 * 60 * 1000;
/** The worker's budget is 10 minutes; a run RUNNING past this is stuck. */
export const RUNNING_STUCK_AFTER_MS = 12 * 60 * 1000;

export const isActive = (run: IClaimResearchRun | null | undefined) =>
  run?.status === "QUEUED" || run?.status === "RUNNING";

const msSince = (iso: string | null | undefined, now: number) =>
  iso ? now - new Date(iso).getTime() : null;

export const queuedFor = (run: IClaimResearchRun, now: number) =>
  msSince(run.createdAt, now) ?? 0;

export const runningFor = (run: IClaimResearchRun, now: number) =>
  msSince(run.startedAt ?? run.createdAt, now) ?? 0;

export const isUnpicked = (run: IClaimResearchRun, now: number) =>
  run.status === "QUEUED" && queuedFor(run, now) > QUEUED_UNPICKED_AFTER_MS;

/**
 * RUNNING past the worker's budget. The run's public shape carries no heartbeat, so unlike the
 * engine explanation this is elapsed time alone.
 */
export const isStuck = (run: IClaimResearchRun, now: number) =>
  run.status === "RUNNING" && runningFor(run, now) > RUNNING_STUCK_AFTER_MS;

/** "40 s", "3 min" — the wait so far. */
export const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min`;
};

/** Model URLs are only ever links when they are http(s); anything else renders as text. */
export const safeHttpUrl = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
};

/** Dollars from the model's line table (already dollars, not cents); text passes through. */
export const formatDollars = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value !== "number") return String(value);
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

export const formatCost = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : null;
};

export const formatRunDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** "Contract text: 1 of 4 documents read", or null when the run predates the status. */
export const contractTextLine = (
  status: IClaimResearchContractTextStatus[] | null | undefined
) => {
  if (!Array.isArray(status)) return null;
  if (status.length === 0)
    return "Contract text: no contract document is attached to this claim's contract terms.";
  const read = status.filter((s) => s.status === "read").length;
  return `Contract text: ${read} of ${status.length} document${
    status.length === 1 ? "" : "s"
  } read`;
};

/**
 * One run in the picker: its date, plus the status while not DONE ("…, 2:10 PM · Running").
 * Nothing else — the verdict and confidence are in the headline, and Mike does not want the cost
 * on the tab (it is in the staff review box's run details).
 */
export const runPickerLabel = (run: IClaimResearchRun) => {
  const date = formatRunDate(run.createdAt);
  if (run.status === "DONE") return date;
  return `${date} · ${
    run.status.charAt(0) + run.status.slice(1).toLowerCase()
  }`;
};

/** Why the verdict form cannot be saved yet, or null when it can. */
export const verdictFormError = (form: {
  agrees: boolean | null;
  correctVerdict: string | null;
  note: string;
}) => {
  if (form.agrees === null) return "Choose Agree or Disagree.";
  if (!form.agrees && !form.correctVerdict)
    return "Pick the correct verdict to disagree.";
  if (form.note.length > NOTE_MAX_CHARS)
    return `The note is over ${NOTE_MAX_CHARS} characters.`;
  return null;
};

/** The API's error sentence from an RTK Query error, if it sent one. */
export const apiErrorText = (error: unknown) => {
  const data = (error as { data?: { error?: unknown; message?: unknown } })
    ?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  return null;
};

export const isDisabledError = (error: unknown) => {
  const e = error as { status?: unknown; data?: { code?: unknown } };
  return e?.status === 503 || e?.data?.code === "CLAIM_RESEARCH_DISABLED";
};
